import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { BarChart3, Search, ExternalLink, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface DashItem {
  titulo: string
  cliente: string
  url: string
}

const DASH_ITEMS: DashItem[] = [
  // Tonal
  { titulo: 'Panel Enriquecido — Base de Datos', cliente: 'Tonal', url: '/dash/panel-enriquecido' },
  // NAOS
  { titulo: 'Segmento NAOS - Dermatología', cliente: 'NAOS', url: '/dash/naos/segmento-0323' },
  // Bago
  { titulo: 'Base de Datos Bago 03/26', cliente: 'Bago', url: '/dash/bago/bd-0326' },
  { titulo: 'Bago Respiratoria 12/25', cliente: 'Bago', url: '/dash/bago/bd/respiratoria1225' },
  // Glenmark
  { titulo: 'Base de Datos Glenmark 03/26', cliente: 'Glenmark', url: '/dash/glenmark/bd-0326' },
  // Saval
  { titulo: 'Base de Datos Saval 08/26', cliente: 'Saval', url: '/dash/saval/bd-0826' },
  // Abbott
  { titulo: 'Abbott Segmento 12/25', cliente: 'Abbott', url: '/dash/abbott/bd/segmento1225' },
  { titulo: 'Abbott BD Segmentada - Ginecología 03/26', cliente: 'Abbott', url: '/dash/abbott/bd-segmentada-3003' },
  // Opella
  { titulo: 'Allegra Bilaxten 2026', cliente: 'Opella', url: '/dash/opella/allegra-bilaxten-2026' },
  { titulo: 'Opella Enero 2026', cliente: 'Opella', url: '/dash/opella/enero-2026' },
  { titulo: 'Opella Dashboard', cliente: 'Opella', url: '/dash/opella/dashboard' },
  { titulo: 'Allegra 02/26', cliente: 'Opella', url: '/dash/opella/allegra-0226' },
  { titulo: 'Novalgina Ecuador', cliente: 'Opella', url: '/dash/opella/novalgina-ecuador' },
  { titulo: 'Probióticos Cruce 01/26', cliente: 'Opella', url: '/dash/opella/probioticos-cruce-0126' },
  { titulo: 'Opella Panamá', cliente: 'Opella', url: '/dash/opella/panama' },
  { titulo: 'Opella Dermatólogos', cliente: 'Opella', url: '/dash/opella/dermatologos' },
  { titulo: 'Muestras Efectividad', cliente: 'Opella', url: '/dash/opella/muestras-efectividad' },
  { titulo: 'Gastro Opella', cliente: 'Opella', url: '/dash/gastro-opella-reporte' },
  // Ferrer
  { titulo: 'Ferrer Médicos Perú 02/18', cliente: 'Ferrer', url: '/dash/ferrer/medicos-peru-0218' },
  { titulo: 'Ferrer Médicos Perú 01/26', cliente: 'Ferrer', url: '/dash/ferrer/medicos-peru-0126' },
  // ODF
  { titulo: 'ODF Farmacias 01/26', cliente: 'ODF', url: '/dash/odf/farmacias-0126' },
  { titulo: 'ODF Segmentación por Producto 07/26', cliente: 'ODF', url: '/dash/odf/segmento-0726' },
  // ELEA
  { titulo: 'ELEA Segmentación por Producto 03/09', cliente: 'ELEA', url: '/dash/elea/segmento-0309' },
  // Tonal
  { titulo: 'Farmacias 05/03', cliente: 'Tonal', url: '/dash/tonal/farmacias-0503' },
  { titulo: 'Base Médicos Tonal 01/26', cliente: 'Tonal', url: '/dash/base-medicos-tonal-0126' },
  { titulo: 'Médicos Especialidades', cliente: 'Tonal', url: '/dash/medicos/especialidades' },
  // Unipharma
  { titulo: 'Unipharma BD 03/26', cliente: 'Unipharma', url: '/dash/unipharma/bd-0326' },
  // Biotoscana
  { titulo: 'Biotoscana Perú', cliente: 'Biotoscana', url: '/dash/biotoscana/a4b2-c8d9-e1f7' },
  // ODT
  { titulo: 'Nerasilda Tadanerfil', cliente: 'ODT', url: '/dash/odt/e3cf-a664-4da1' },
  { titulo: 'BD ODT 14/10/25', cliente: 'ODT', url: '/dash/odt/f7b2-9e45-3c18' },
  // Exeltis
  { titulo: 'Exeltis Ginecólogos Perú', cliente: 'Exeltis', url: '/dash/exeltis/ginecologos' },
  // Panamá
  { titulo: 'Panamá 02/26', cliente: 'Panamá', url: '/dash/panama/0226' },

  // Difare
  { titulo: 'Difare Cardiólogos', cliente: 'Difare', url: '/dash/difare/cardiologos' },
]

export function DashAdminPage() {
  const [search, setSearch] = useState('')
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const baseUrl = window.location.origin

  const filtered = DASH_ITEMS.filter(item => {
    const q = search.toLowerCase()
    return item.titulo.toLowerCase().includes(q) || item.cliente.toLowerCase().includes(q)
  })

  const clientes = [...new Set(DASH_ITEMS.map(d => d.cliente))].sort()

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(`${baseUrl}${url}`)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-black dark:text-white" />
          <h1 className="text-2xl font-bold">Dash Reports</h1>
          <span className="text-sm text-muted-foreground">({DASH_ITEMS.length} reportes)</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{DASH_ITEMS.length}</div>
            <div className="text-sm text-muted-foreground">Total Reportes</div>
          </CardContent>
        </Card>
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{clientes.length}</div>
            <div className="text-sm text-muted-foreground">Clientes</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por título o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                <TableHead className="w-[200px]">Cliente</TableHead>
                <TableHead>Título</TableHead>
                <TableHead className="w-[350px]">URL</TableHead>
                <TableHead className="w-[120px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.url} className="hover:bg-gray-50 dark:hover:bg-[hsl(220,15%,16%)]">
                  <TableCell className="font-medium">{item.cliente}</TableCell>
                  <TableCell>{item.titulo}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {item.url}
                    </code>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Copiar URL completa"
                        onClick={() => handleCopy(item.url)}
                      >
                        {copiedUrl === item.url
                          ? <Check className="h-4 w-4 text-green-500" />
                          : <Copy className="h-4 w-4" />
                        }
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Abrir reporte"
                        onClick={() => window.open(item.url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No se encontraron reportes
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
